import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import OptionMenuWithError from "../OptionMenuWithError";
import LocationLink from "../LocationLink";
import "../messageStyles.css";
import { resendMessage } from "@/features/chat/chatSlice";
import {
  selectCurrConversation,
  selectConversations,
} from "@/features/control/controlSlice";
import { Conversation } from "@/types/conversation";
import { useCallback } from "react";
import { isEncryptedMessage } from "@/utils/crypto";
import { Lock, Satellite } from "lucide-react";

interface SentTextMessageProps {
  id: number;
  created: string;
  time: string;
  msg: string;
  error?: boolean;
  /** Set when Redux already knows this was sent encrypted (body may be plaintext in state). */
  isEncrypted?: boolean;
  isSatellite?: boolean;
  lat?: string;
  lng?: string;
}

const SentTextMessage = ({
  id,
  created,
  time,
  msg,
  error,
  lat,
  lng,
  isEncrypted,
  isSatellite,
}: SentTextMessageProps) => {
  const dispatch = useAppDispatch();
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const conversations: Conversation[] = useAppSelector(selectConversations);
  const contentIsEncryptedBlob = isEncryptedMessage(msg);
  const showEncryptedIndicator = isEncrypted || contentIsEncryptedBlob;
  const displayText = contentIsEncryptedBlob ? "🔒 [Encrypted message]" : msg;

  const handleResend = useCallback(async () => {
    const addressId = conversations[currConversationIndex!].addressId;
    const address = conversations[currConversationIndex!].address;
    const addressType = conversations[currConversationIndex!].addressType;

    await dispatch(
      resendMessage({ address, addressId, addressType, message: msg })
    );
  }, [dispatch, conversations, currConversationIndex]);

  return (
    <div key={id} className="message-container">
      <div className="message-time-container items-end">
        <span className="message-time">{created ? time : "now"}</span>
        <OptionMenuWithError
          error={error}
          handleResend={handleResend}
          lockNode={showEncryptedIndicator ? <Lock size={12} className="text-green-500 shrink-0 mr-2" /> : undefined}
        >
          <div className="message-text-container sent-color">
            <p className="message-text">{displayText}</p>
          </div>
        </OptionMenuWithError>
        {isSatellite && <Satellite size={15} className="text-blue-400 shrink-0" />}
        <LocationLink lat={lat} lng={lng} />
      </div>
    </div>
  );
};

export default SentTextMessage;
